import { forwardRef } from 'react';

import { YStack } from '@/components/ui/layout/layout';
import { ScrollMask } from '@/components/ui/ScrollMask';
import { Tooltip } from '@/components/ui/Tooltip';
import { type ShortcutId } from '@/features/shortcuts/shortcuts.registry';
import { createSafeContext } from '@/hooks/createContextStore.hooks';
import { cn } from '@/lib/utils';

const RAIL_WIDTH = 72;
const RAIL_COMPACT_WIDTH = 64;

const railGap = (labels: boolean) => (labels ? 'gap-3' : 'gap-4');

const [RailProvider, useRailContext] = createSafeContext<{ labels: boolean }>(
  'Rail.Button must be used within Rail.Root'
);

export const Root = ({
  children,
  labels = false,
  className,
}: {
  children: React.ReactNode;
  labels?: boolean;
  className?: string;
}) => (
  <RailProvider value={{ labels }}>
    <YStack
      className={cn(
        'shell ambient-sidebar h-full shrink-0 items-center pb-3',
        railGap(labels),
        className
      )}
      style={{ width: labels ? RAIL_WIDTH : RAIL_COMPACT_WIDTH }}
    >
      {children}
    </YStack>
  </RailProvider>
);

export const Scroll = ({ children }: { children: React.ReactNode }) => {
  const { labels } = useRailContext();

  return (
    <ScrollMask
      showEndFade
      fadeSize={24}
      className={cn(
        'flex w-full flex-1 flex-col items-center overflow-x-hidden pb-1 scrollbar-hide',
        labels ? 'pt-[13px]' : 'pt-[15px]',
        railGap(labels)
      )}
    >
      {children}
    </ScrollMask>
  );
};

type ButtonProps = {
  label: string;
  caption?: string | false;
  active?: boolean;
  hotkey?: ShortcutId;
  badge?: React.ReactNode;
  onIntent?: () => void;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      label,
      caption = label,
      active,
      hotkey,
      badge,
      onIntent,
      onPointerEnter,
      onFocus,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const { labels } = useRailContext();
    const showCaption = labels && caption !== false;

    return (
      <Tooltip
        label={label}
        hotkey={hotkey}
        side="right"
        sideOffset={8}
        disabled={props['aria-expanded'] === true}
      >
        <button
          ref={ref}
          type="button"
          aria-label={label}
          aria-current={active ? 'page' : undefined}
          className={cn(
            'group/rail-button relative flex shrink-0 flex-col items-center justify-center rounded-xl focus-ring',
            'text-foreground-subtle hover:text-foreground',
            showCaption && 'w-16 gap-1 pb-1 !bg-transparent',
            !showCaption && (labels ? 'size-10' : 'size-9'),
            !showCaption && 'hover:bg-surface-hover',
            !showCaption &&
              active &&
              'bg-surface-active hover:bg-surface-active',
            active && 'text-foreground',
            className
          )}
          onPointerEnter={(event) => {
            onIntent?.();
            onPointerEnter?.(event);
          }}
          onFocus={(event) => {
            onIntent?.();
            onFocus?.(event);
          }}
          {...props}
        >
          <span
            className={cn(
              'relative flex items-center justify-center rounded-xl [&>svg]:size-[18px]',
              labels ? 'size-10' : 'size-9',
              showCaption && 'group-hover/rail-button:bg-surface-hover',
              showCaption &&
                active &&
                'bg-surface-active group-hover/rail-button:bg-surface-active'
            )}
          >
            {children}
            {badge}
          </span>
          {showCaption && (
            <span className="text-center text-[0.75rem] leading-none whitespace-nowrap">
              {caption}
            </span>
          )}
        </button>
      </Tooltip>
    );
  }
);
Button.displayName = 'Rail.Button';
